// src/hooks/useIntersectionObserver.ts
import { useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';

interface IntersectionObserverArgs {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
}

/**
 * A reusable hook to detect if an element is in the viewport using the Intersection Observer API.
 */
export function useIntersectionObserver(
  options: IntersectionObserverArgs = {}
): [RefObject<HTMLElement>, boolean] {
  const ref = useRef<HTMLElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const normalizedOptions = useMemo(
    () => ({
      root: options.root ?? null,
      rootMargin: options.rootMargin ?? '0px',
      threshold: options.threshold ?? 0,
    }),
    [options.root, options.rootMargin, options.threshold]
  );

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsIntersecting(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      normalizedOptions
    );

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
      observer.disconnect();
    };
  }, [normalizedOptions]);

  return [ref, isIntersecting];
}
